import { environment } from '../../environments/environment';
import { User } from '../interfaces';

const ADMIN_PUBLIC_ROUTES = ['/login', '/fremium'];

export interface PortalAccessUser {
  firstAccess?: boolean;
  is_onboarding_complete?: boolean;
  platformAccessBlocked?: boolean;
  cpf?: string;
  status?: string | { description?: string; name?: string; code?: string };
  companyId?: string;
  companies?: Array<{ id?: string; contractModel?: string; name?: string; companyName?: string; company_name?: string }>;
  freemiumSubscription?: { status?: string } | null;
  customer?: { cpf?: string; status?: string | { description?: string } };
}

const PUBLIC_ROUTES = ['/login', '/fremium', '/assinatura-suspensa', '/dados-cadastro', '/migracao-conta'];

const SUSPENDED_ALLOWED_ROUTES = [
  '/assinatura-suspensa',
  '/assinaturas',
  '/checkout-fremium',
  '/fremium-success',
];

const ONBOARDING_ROUTES = ['/bem-vindo', '/bem-vindo-fremium'];

const FREEMIUM_FLOW_ROUTES = [
  '/bem-vindo',
  '/bem-vindo-fremium',
  '/selecionar-plano',
  '/checkout-fremium',
  '/fremium-success',
];

const NO_SUBSCRIPTION_ALLOWED_ROUTES = [
  '/dados-cadastro',
  '/bem-vindo',
  '/bem-vindo-fremium',
  '/selecionar-plano',
  '/checkout-fremium',
  '/fremium-success',
];

const MIGRATION_ALLOWED_ROUTES = [
  '/migracao-conta',
  '/selecionar-plano',
  '/checkout-fremium',
  '/fremium-success',
  '/bem-vindo',
  '/bem-vindo-fremium',
];

export function normalizeRoutePath(url: string): string {
  const path = (url || '/').split('?')[0].split('#')[0];
  return path || '/';
}

function normalizePortalRoutePath(path: string): string {
  return normalizeRoutePath(path);
}

function normalizeStatusValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    return trimmed || undefined;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      normalizeStatusValue(record.description) ||
      normalizeStatusValue(record.name) ||
      normalizeStatusValue(record.code) ||
      normalizeStatusValue(record.value)
    );
  }

  return undefined;
}

function resolveUserAccountStatus(user: PortalAccessUser | null | undefined): string | undefined {
  if (!user) {
    return undefined;
  }

  const record = user as Record<string, unknown>;
  const account = record.account as Record<string, unknown> | undefined;
  const customer = user.customer as Record<string, unknown> | undefined;

  return (
    normalizeStatusValue(user.status) ||
    normalizeStatusValue(record.accountStatus) ||
    normalizeStatusValue(account && account.status) ||
    normalizeStatusValue(customer && customer.status)
  );
}

function isUserAccountBlocked(user: PortalAccessUser | null | undefined): boolean {
  const status = resolveUserAccountStatus(user);
  return status === 'blocked' || status === 'bloqueado';
}

/**
 * Bloqueio de plataforma vindo do /me: fatura da empresa em aberto.
 * Enquanto true o usuário não navega na plataforma.
 */
export function isPlatformAccessBlocked(user: PortalAccessUser | null | undefined): boolean {
  return !!user && user.platformAccessBlocked === true;
}

function isFreemiumCompany(company?: { contractModel?: string }): boolean {
  if (!company || !company.contractModel) {
    return false;
  }
  return company.contractModel === 'b2c' || company.contractModel === 'coparticipation';
}

export function getActiveCompany(
  user: PortalAccessUser
): { id?: string; contractModel?: string; name?: string; companyName?: string; company_name?: string } | undefined {
  const companies = user.companies || [];
  if (!companies.length) {
    return undefined;
  }

  if (user.companyId) {
    const activeCompany = companies.find(function(company) {
      return company.id === user.companyId;
    });
    if (activeCompany) {
      return activeCompany;
    }
  }

  return companies[0];
}

export function getCompanyDisplayName(user: PortalAccessUser | null | undefined): string {
  const company = user ? getActiveCompany(user) : undefined;

  if (!company) {
    return 'sua empresa';
  }

  if (company.name) {
    return company.name;
  }

  if (company.companyName) {
    return company.companyName;
  }

  if (company.company_name) {
    return company.company_name;
  }

  return 'sua empresa';
}

export function getPlatformAccessPendingMessage(user: PortalAccessUser | null | undefined): string {
  const companyName = getCompanyDisplayName(user);

  return 'Ainda consta pendência na fatura. Se você pagou agora, o ' + companyName + ' pode levar algumas horas para confirmar.';
}

function hasFreemiumProfile(user: PortalAccessUser): boolean {
  const companies = user.companies || [];
  for (let i = 0; i < companies.length; i++) {
    if (isFreemiumCompany(companies[i])) {
      return true;
    }
  }

  return !!user.freemiumSubscription;
}

function isSubscriptionSuspended(user: PortalAccessUser): boolean {
  const status = user.freemiumSubscription && user.freemiumSubscription.status;
  return status === 'pending_payment';
}

function isBlockedFreemiumUser(user: PortalAccessUser | null | undefined): boolean {
  if (!user || !isUserAccountBlocked(user)) {
    return false;
  }

  return hasFreemiumProfile(user);
}

function needsOnboarding(user: PortalAccessUser | null | undefined): boolean {
  if (!user) {
    return false;
  }

  if (user.is_onboarding_complete === true) {
    return false;
  }

  if (user.is_onboarding_complete === false) {
    return true;
  }

  return user.firstAccess === true;
}

function getPortalAccessRedirectPath(
  user: PortalAccessUser | null | undefined,
  currentPath: string
): string | null {
  if (!user) {
    return null;
  }

  const path = normalizePortalRoutePath(currentPath);

  if (PUBLIC_ROUTES.includes(path)) {
    return null;
  }

  if (isBlockedFreemiumUser(user)) {
    if (!MIGRATION_ALLOWED_ROUTES.includes(path)) {
      return '/migracao-conta';
    }
    return null;
  }

  if (!user.companies || !user.companies.length) {
    return null;
  }

  const activeCompany = getActiveCompany(user);
  const isCompanyFremium = isFreemiumCompany(activeCompany);

  if (isSubscriptionSuspended(user)) {
    if (!SUSPENDED_ALLOWED_ROUTES.includes(path)) {
      return '/assinatura-suspensa';
    }
    return null;
  }

  if (hasMissingRegistrationData(user as User)) {
    if (path !== '/dados-cadastro') {
      return '/dados-cadastro';
    }
    return null;
  }

  if (needsOnboarding(user)) {
    if (ONBOARDING_ROUTES.indexOf(path) === -1) {
      return '/bem-vindo';
    }
    return null;
  }

  if (typeof window !== 'undefined' && localStorage.getItem('fremiumFlowBlocked') === 'true' && isCompanyFremium) {
    if (FREEMIUM_FLOW_ROUTES.indexOf(path) === -1) {
      return '/selecionar-plano';
    }
    return null;
  }

  if (isCompanyFremium && !user.freemiumSubscription) {
    if (NO_SUBSCRIPTION_ALLOWED_ROUTES.indexOf(path) === -1) {
      return '/selecionar-plano';
    }
    return null;
  }

  return null;
}

export function hasMissingRegistrationData(user: User | null | undefined): boolean {
  if (!user) {
    return false;
  }

  const customer = (user as any).customer;
  const cpf = String(user.cpf || (customer && customer.cpf) || '').trim();
  return cpf.length === 0;
}

export function syncSubscriptionSuspendedFlag(user: User | null | undefined): void {
  if (typeof window === 'undefined' || !user) {
    return;
  }

  if (user.freemiumSubscription && user.freemiumSubscription.status === 'pending_payment') {
    localStorage.setItem('accountSuspended', 'true');
  } else {
    localStorage.removeItem('accountSuspended');
  }
}

export function redirectToPortalPath(path: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  window.location.href = `${environment.PORTAL_URL}${normalizedPath}`;
}

export function resolveAccessRedirect(user: User | null | undefined, currentUrl: string): string | null {
  if (!user) {
    return null;
  }

  const currentPath = normalizeRoutePath(currentUrl);

  if (ADMIN_PUBLIC_ROUTES.includes(currentPath)) {
    return null;
  }

  syncSubscriptionSuspendedFlag(user);

  const portalRedirect = getPortalAccessRedirectPath(user as PortalAccessUser, currentPath);
  if (portalRedirect) {
    return portalRedirect;
  }

  if (hasMissingRegistrationData(user) && currentPath !== '/dados-cadastro') {
    try {
      localStorage.setItem('intendedRoute', currentPath);
    } catch (e) {
      // ignore
    }
    return '/dados-cadastro';
  }

  return null;
}

export function applyAccessRedirect(user: User | null | undefined, currentUrl: string): boolean {
  const redirectPath = resolveAccessRedirect(user, currentUrl);

  if (!redirectPath) {
    return false;
  }

  redirectToPortalPath(redirectPath);
  return true;
}
